import React from 'react';
import { ResponsiveContainer, LineChart, Line } from 'recharts';
import { cn } from '@/lib/utils';
import { CHART } from '../../lib/reporting-format';

/**
 * Shared reporting chart chrome. Every chart/table on the redesigned tabs sits
 * inside a `<ChartCard>` and renders an `<EmptyChart>` when it has no data, so
 * the empty state is consistent (per DESIGN.md §15.2 / §14).
 */

interface ChartCardProps {
  title: React.ReactNode;
  /** Optional right-aligned control (toggle, legend, range chip). */
  action?: React.ReactNode;
  description?: React.ReactNode;
  className?: string;
  children: React.ReactNode;
}

export const ChartCard: React.FC<ChartCardProps> = ({
  title,
  action,
  description,
  className,
  children,
}) => (
  <section className={cn('bg-card rounded-lg border p-6', className)}>
    <div className="flex items-start justify-between gap-3 mb-4">
      <div className="min-w-0 break-words">
        <h3 className="text-base font-semibold leading-tight">{title}</h3>
        {description && (
          <p className="text-xs text-muted-foreground mt-1">{description}</p>
        )}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
    {children}
  </section>
);

interface EmptyChartProps {
  message: string;
  className?: string;
}

export const EmptyChart: React.FC<EmptyChartProps> = ({ message, className }) => (
  <div
    className={cn(
      'flex h-full min-h-[12rem] items-center justify-center rounded-md bg-muted/10 px-4 text-center',
      className,
    )}
  >
    <p className="text-sm text-muted-foreground">{message}</p>
  </div>
);

interface SparklineProps {
  data: Array<{ date: string; units: number }>;
  /** Stroke colour; defaults to brand purple. */
  color?: string;
  height?: number;
  className?: string;
  ariaLabel?: string;
}

/**
 * Tiny trend line for scorecard tiles. No axes, no grid — just the shape of the
 * trend. Renders nothing visible (an inert spacer) when there are < 2 points.
 */
export const Sparkline: React.FC<SparklineProps> = ({
  data,
  color = CHART.purple,
  height = 36,
  className,
  ariaLabel,
}) => {
  if (data.length < 2) {
    return <div style={{ height }} className={className} aria-hidden="true" />;
  }
  return (
    <div className={className} style={{ height }} role="img" aria-label={ariaLabel}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 4, right: 2, bottom: 0, left: 2 }}>
          <Line
            type="monotone"
            dataKey="units"
            stroke={color}
            strokeWidth={2}
            dot={false}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};
